/**
 * In-app hardening.
 *
 * Read this first: none of it can stop the Android home gesture, the recents
 * gesture, the notification shade, or the power button. No web API can. The
 * actual lock is Android's App pinning, enabled per device - see README.md.
 *
 * What this file does is remove every way the *app itself* could navigate away
 * or reveal browser UI when a phone is being mashed at random.
 */

/** Ignore synthetic events. A trusted event came from a real finger. */
const isReal = (e: Event) => e.isTrusted;

export function installLockdown(): void {
  trapBackNavigation();
  suppressBrowserGestures();
  keepScreenAwake();
  requestFullscreenOnFirstTouch();
  lockLandscape();
}

/**
 * The manifest already asks for landscape, which is what an installed WebAPK
 * honours. This is the belt-and-braces version for the browser-tab case, where
 * the manifest is ignored - it only works once something has taken fullscreen,
 * so failures are expected and ignored.
 */
function lockLandscape(): void {
  const orientation = screen.orientation as ScreenOrientation & {
    lock?(orientation: string): Promise<void>;
  };
  const attempt = () => orientation?.lock?.('landscape').catch(() => {});
  void attempt();
  document.addEventListener('fullscreenchange', () => {
    if (document.fullscreenElement) void attempt();
  });
}

/**
 * The app has no routes, so "back" has nothing to go back to - which means in a
 * standalone WebAPK it closes the app. A stack of sentinel history entries,
 * refilled on every popstate, turns the back gesture into a no-op instead.
 *
 * A stack rather than a single entry because back can fire several times before
 * the first popstate is delivered. Each sentinel records its own depth in
 * history.state, so the handler can tell how much of the buffer has been eaten
 * and top it back up - without pushing on every event, which would grow history
 * without bound and eventually hit Chrome's navigation throttle.
 */
const TRAP_DEPTH = 6;

function trapBackNavigation(): void {
  const fill = (from: number) => {
    for (let depth = from + 1; depth <= TRAP_DEPTH; depth++) {
      history.pushState({ trap: depth }, '', location.href);
    }
  };
  fill(0);

  globalThis.addEventListener('popstate', (e) => {
    // state is null once we have fallen back onto the app's own first entry:
    // the next back would close the app, so refill from empty.
    const depth = (e.state as { trap?: number } | null)?.trap ?? 0;
    if (depth < TRAP_DEPTH) fill(depth);
  });
}

function suppressBrowserGestures(): void {
  // All touch belongs to the viewer. Without this, multi-finger and edge drags
  // reach the browser as scroll, pull-to-refresh, or page zoom.
  document.addEventListener(
    'touchmove',
    (e) => {
      // Opt-out for UI that needs its own touch handling, such as the dev
      // controls. Nothing in the shipped app sets it.
      if ((e.target as Element | null)?.closest?.('[data-allow-touch]')) return;
      if (isReal(e)) e.preventDefault();
    },
    { passive: false },
  );

  // Long-press "copy/share" bubble, text selection, image drag.
  document.addEventListener('contextmenu', (e) => e.preventDefault());
  document.addEventListener('selectstart', (e) => e.preventDefault());
  document.addEventListener('dragstart', (e) => e.preventDefault());

  // Double-tap page zoom. maximum-scale in the viewport tag covers most of it;
  // this covers the rest, including Chrome's ignore-viewport accessibility mode.
  let lastTouchEnd = 0;
  document.addEventListener(
    'touchend',
    (e) => {
      const now = Date.now();
      // Not on real controls: preventing touchend there would also swallow the
      // synthesized click, making the panel's buttons miss fast repeat taps.
      const onControl = (e.target as Element | null)?.closest?.('button, a, input, select');
      if (!onControl && now - lastTouchEnd < 320) e.preventDefault();
      lastTouchEnd = now;
    },
    { passive: false },
  );

  // Pinch on the *page* (as opposed to the canvas, which OrbitControls owns).
  document.addEventListener(
    'gesturestart',
    (e) => e.preventDefault(),
    { passive: false } as AddEventListenerOptions,
  );

  // Keyboard escapes, for the case where a hardware or on-screen keyboard is
  // somehow attached: F5, Ctrl+R, Backspace-as-back, Alt+Left.
  globalThis.addEventListener('keydown', (e) => {
    const blocked = e.key === 'F5' ||
      (e.ctrlKey && (e.key === 'r' || e.key === 'R')) ||
      e.key === 'Backspace' ||
      (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight'));
    if (blocked) e.preventDefault();
  });
}

/**
 * Hold the screen on while the app is in front. The lock is dropped by the OS
 * whenever the page is hidden, so it has to be re-acquired on every return.
 */
function keepScreenAwake(): void {
  const wakeLock = (navigator as Navigator & {
    wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
  }).wakeLock;
  if (!wakeLock) return;

  let sentinel: { release(): Promise<void> } | undefined;
  const acquire = async () => {
    if (sentinel || document.visibilityState !== 'visible') return;
    try {
      sentinel = await wakeLock.request('screen');
    } catch {
      // Denied (low battery, or backgrounded mid-request). Not fatal.
    }
  };
  void acquire();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void acquire();
    } else {
      // The OS has already dropped the lock; just forget our stale handle so
      // the next foreground pass asks for a fresh one.
      sentinel = undefined;
    }
  });
}

/**
 * `display: fullscreen` in the manifest already hides the status bar for the
 * installed app. This only matters when it is opened as a plain browser tab -
 * fullscreen then hides the URL bar too. It must be triggered by a gesture.
 */
function requestFullscreenOnFirstTouch(): void {
  // In dev the first click is usually aimed at the debug controls, and having it
  // throw the window into fullscreen makes them unusable.
  if (import.meta.env.DEV) return;

  const go = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
    document.removeEventListener('touchend', go);
    document.removeEventListener('click', go);
  };
  document.addEventListener('touchend', go);
  document.addEventListener('click', go);
}

/**
 * Long press in the top-left corner reveals the adult panel. Deliberately
 * awkward: a small target, held for three seconds, that cancels the moment the
 * finger drifts. A child holding the phone will not find it by accident.
 */
export function onSecretGesture(callback: () => void): void {
  const CORNER = 72; // px
  const HOLD = 3000; // ms
  const DRIFT = 24; // px
  let timer: number | undefined;
  let origin: { x: number; y: number } | undefined;

  const cancel = () => {
    globalThis.clearTimeout(timer);
    timer = undefined;
    origin = undefined;
  };

  document.addEventListener('pointerdown', (e) => {
    if (e.clientX > CORNER || e.clientY > CORNER) return;
    origin = { x: e.clientX, y: e.clientY };
    timer = globalThis.setTimeout(() => {
      cancel();
      callback();
    }, HOLD);
  });

  document.addEventListener('pointermove', (e) => {
    if (!origin) return;
    if (Math.hypot(e.clientX - origin.x, e.clientY - origin.y) > DRIFT) cancel();
  });

  document.addEventListener('pointerup', cancel);
  document.addEventListener('pointercancel', cancel);
}
